/**
 * Shared helpers for the theme's web components.
 *
 * Kept intentionally small: only the utilities that components actually import.
 * Add to this file when a second component needs the same helper — not before.
 */

/**
 * Schedules a callback during idle periods, or falls back to setTimeout.
 * @type {(callback: IdleRequestCallback, options?: IdleRequestOptions) => number}
 */
export const requestIdleCallback =
  typeof window.requestIdleCallback === 'function'
    ? (cb, opts) => window.requestIdleCallback(cb, opts)
    : (cb) => window.setTimeout(cb, 0);

/**
 * Builds a fetch config for Shopify's AJAX endpoints.
 * @param {'json' | 'javascript'} [type] The expected response type.
 * @param {{ headers?: Record<string, string>, body?: BodyInit }} [config] Extra headers and the request body.
 * @returns {RequestInit} A config object to pass to fetch.
 */
export function fetchConfig(type = 'json', config = {}) {
  /** @type {Record<string, string>} */
  const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers,
    body: config.body,
  };
}

/**
 * Creates a debounced function that delays calling fn until after wait milliseconds have
 * elapsed since the last invocation. The returned function has a .cancel() method.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to debounce.
 * @param {number} wait The time (in milliseconds) to wait before calling fn.
 * @returns {T & { cancel(): void }} A debounced version of fn.
 */
export function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timeout;

  /** @param {...any} args */
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }

  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return /** @type {T & { cancel(): void }} */ (debounced);
}

/**
 * Creates a throttled function that calls fn at most once per delay milliseconds.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to throttle.
 * @param {number} delay The time (in milliseconds) between calls.
 * @returns {T & { cancel(): void }} A throttled version of fn.
 */
export function throttle(fn, delay) {
  let lastCall = 0;

  /** @param {...any} args */
  function throttled(...args) {
    const now = performance.now();

    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }

  throttled.cancel = () => {
    lastCall = performance.now();
  };

  return /** @type {T & { cancel(): void }} */ (throttled);
}

/** @type {MediaQueryList} */
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Check if the user prefers reduced motion.
 * @returns {boolean} True if the user prefers reduced motion.
 */
export function prefersReducedMotion() {
  return reducedMotion.matches;
}

/**
 * Calls the callback once the DOM is parsed, immediately if it already is.
 * @param {() => void} callback The function to call.
 * @param {AddEventListenerOptions} [options] Options passed to addEventListener.
 */
export function onDocumentReady(callback, options) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, options);
  } else {
    callback();
  }
}

/**
 * Calls the callback once every resource has loaded, immediately if they already have.
 * @param {() => void} callback The function to call.
 */
export function onDocumentLoaded(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback);
  }
}

/**
 * Elements currently holding the root scroll lock.
 *
 * Reference counted so nested overlays (a drawer opening a dialog) don't release
 * the lock early when only one of them closes.
 *
 * @type {Set<Element>}
 */
const scrollLockOwners = new Set();

/** @type {MutationObserver | undefined} */
let scrollLockObserver;

function pruneDisconnectedScrollLockOwners() {
  for (const owner of scrollLockOwners) {
    if (!owner.isConnected) {
      scrollLockOwners.delete(owner);
    }
  }
}

function syncScrollLock() {
  pruneDisconnectedScrollLockOwners();

  if (scrollLockOwners.size > 0) {
    document.documentElement.setAttribute('scroll-lock', '');

    if (!scrollLockObserver) {
      scrollLockObserver = new MutationObserver(syncScrollLock);
      scrollLockObserver.observe(document.documentElement, { childList: true, subtree: true });
    }

    return;
  }

  document.documentElement.removeAttribute('scroll-lock');
  scrollLockObserver?.disconnect();
  scrollLockObserver = undefined;
}

/**
 * Locks root scrolling for an owning element.
 * @param {Element} owner The element that owns the scroll lock.
 */
export function lockScroll(owner) {
  scrollLockOwners.add(owner);
  syncScrollLock();
}

/**
 * Unlocks root scrolling for an owning element.
 * @param {Element} owner The element that owns the scroll lock.
 */
export function unlockScroll(owner) {
  scrollLockOwners.delete(owner);
  syncScrollLock();
}

/**
 * Check if a point is within an element.
 * @param {number} x The x coordinate of the point.
 * @param {number} y The y coordinate of the point.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the point is within the element.
 */
export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();

  return x >= left && x <= right && y >= top && y <= bottom;
}

/**
 * Check if a click landed outside the element.
 *
 * Dialogs report themselves as the target for clicks on their backdrop, so those
 * fall back to a coordinate check against the element's box.
 *
 * @param {MouseEvent} event The mouse event.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the click is outside the element.
 */
export function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }

  return !element.contains(event.target);
}

/**
 * A media query matching the theme's desktop breakpoint.
 * Keep in sync with --breakpoint-lg in base.css.
 * @type {MediaQueryList}
 */
export const mediaQueryLarge = matchMedia('(min-width: 990px)');

/**
 * Check if the current breakpoint is mobile.
 * @returns {boolean} True if below the desktop breakpoint.
 */
export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}

/**
 * Check if the current breakpoint is desktop.
 * @returns {boolean} True if at or above the desktop breakpoint.
 */
export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}

/**
 * Check if the device is a touch device, independently of screen size.
 * @returns {boolean} True if the device reports touch support.
 */
export function isTouchDevice() {
  return 'ontouchstart' in window && navigator.maxTouchPoints > 0;
}

/**
 * Clamps a number between a minimum and maximum value.
 * @param {number} value The input number to clamp.
 * @param {number} min The minimum value.
 * @param {number} max The maximum value.
 * @returns {number} The clamped value.
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}
